import { useCallback, useEffect, useRef, JSX, createElement } from 'react';
import type { ComponentType } from 'react';
import type { FilterProps } from './RepositoryTable';

// Generic: тип репозитория, с которым фильтр работает
export type RepositoryFilterComponentProps<TRepo> = {
  filter: (fn: ((repo: TRepo) => TRepo) | null) => void;
  disabled?: boolean;
  fromUrl: () => string | null;
  toUrl: (value: string | null) => void;
  /**
   * Подписка на изменения состояния таблицы (фильтры/сортировка/страница и т.п.).
   * Полезно когда поведение фильтра зависит от значения другого (sibling) фильтра —
   * например, список «Статус» зависит от выбранного «Типа лида».
   */
  subscribe: (listener: () => void) => () => void;
  /**
   * Читает текущее значение соседнего фильтра по его ключу
   * (без неймспейс-префикса таблицы — формируется внутри: `${tableId}_f_${key}`).
   */
  readSiblingUrl: (key: string) => string | null;
  /**
   * Возвращает текущий репозиторий таблицы со ВСЕМИ активными фильтрами,
   * кроме собственного фильтра этого компонента. Полезно для счётчиков:
   * вызвать кастомный метод репозитория (например, `getDueCounts()`) поверх
   * фильтров таблицы — без дублирования логики применения других фильтров.
   */
  getFilteredRepository: () => TRepo;
};

/**
 * useRepositoryFilter — фабрика фильтров для RepositoryTable с поддержкой URL-ключа.
 *
 * Назначение:
 * - Связать UI‑фильтр с контроллером таблицы;
 * - Дать фильтру единый стабильный ключ `key` для:
 *   — идентификации фильтра в RepositoryController.filter(key, ...)
 *   — чтения/записи параметра в URL: `${tableId}_f_${key}` (через fromUrl/toUrl).
 *
 * Обязанности сторон:
 * - Компонент фильтра САМ решает как применить value к репозиторию (через props.filter(transformer|null));
 * - Контроллер отвечает только за синхронизацию URL и вкладки (replaceState + TabController.setUrl/activate).
 *
 * Параметры:
 * - key: string — стабильный ключ фильтра (например: 'status', 'manager', 'partner', 'id');
 * - component: React.FC — реализация UI фильтра. Получает пропсы:
 *   — filter: (transformer|null) => void — применяет/снимает фильтр на репозитории;
 *   — disabled?: boolean — заблокировать UI (например, во время загрузки);
 *   — fromUrl: () => string|null — прочитать значение фильтра из URL для данного key;
 *   — toUrl: (value: string|null) => void — записать значение фильтра в URL (null удаляет параметр).
 *
 * Поведение URL:
 * - Имена параметров фильтров неймспейсируются tableId: `${tableId}_f_${key}` (tableId — prop `id` у RepositoryTable);
 * - Запись в URL выполняется без навигации (history.replaceState), вкладка синхронизируется автоматически.
 *
 * Поведение пагинации:
 * - При пользовательском изменении фильтра обёртка автоматически возвращает
 *   таблицу на 1-ю страницу (иначе на странице 5 после смены фильтра пользователь
 *   видит «пусто», хотя данные есть с 1-й).
 * - При первичной установке фильтра на mount (восстановление из URL) пагинация
 *   НЕ сбрасывается, иначе теряется страница из URL. Различие между
 *   mount-восстановлением и пользовательским действием решается внутри обёртки —
 *   компоненту фильтра ничего знать про это не нужно.
 *
 * Типичный сценарий в компоненте фильтра:
 * - На mount: const v = fromUrl(); если v есть — выставить локальный state и вызвать filter(repo => ...);
 * - На change: setState(v) → filter(v ? repo => ... : null) → toUrl(v ?? null).
 *
 * Пример:
 *   export const FilterByStatus = useRepositoryFilter<PartnerRepository>('status', ({ filter, disabled, fromUrl, toUrl }) => {
 *     const [value, setValue] = useState<string | null>(null);
 *     useEffect(() => { const v = fromUrl(); if (v) { setValue(v); filter(repo => repo.where('status', '=', v)); } }, [fromUrl, filter]);
 *     const onChange = (v: string | null) => { setValue(v); filter(v ? repo => repo.where('status', '=', v) : null); toUrl(v); };
 *     return <Select value={value} onChange={onChange} disabled={disabled} />;
 *   });
 *
 * Примечания:
 * - fromUrl/toUrl работают ТОЛЬКО для переданного key; сериализация сложных значений — ответственность фильтра (при
 *   необходимости можно сериализовать в одну строку JSON самостоятельно до вызова toUrl).
 */
export interface RepositoryFilterOptions {
  /** Значение по умолчанию — используется если в URL нет параметра фильтра */
  defaultValue?: string;
}

export function useRepositoryFilter<TRepo>(
  key: string,
  component: ComponentType<RepositoryFilterComponentProps<TRepo>>,
  options?: RepositoryFilterOptions
) {
  const Wrapped = ({ controller, disabled }: FilterProps): JSX.Element => {
    // Различаем mount-восстановление из URL и пользовательское изменение фильтра.
    // На mount-цикле UI-фильтр может вызвать boundFilter из своего useEffect
    // (читая значение из URL) — пагинацию трогать нельзя, иначе потеряем
    // восстановленную страницу. После того как mount-цикл этого wrapper'а
    // завершился (наш useEffect ниже), любые вызовы — это уже действия
    // пользователя, и пагинацию надо сбрасывать на 1-ю страницу.
    //
    // Почему useEffect, а не «первый вызов»: фильтр, у которого в URL ничего
    // не было, не вызывает boundFilter на mount — и первый клик пользователя
    // тогда ошибочно классифицируется как «mount-restore». useEffect
    // wrapper'а гарантированно отрабатывает ПОСЛЕ child-эффектов того же
    // mount-цикла и ДО любого пользовательского взаимодействия.
    const isMountPhaseRef = useRef(true);
    useEffect(() => {
      isMountPhaseRef.current = false;
    }, []);

    // filter связываем со стабильным ключом
    const boundFilter = useCallback((fn: ((repo: TRepo) => TRepo) | null) => {
      const resetPage = !isMountPhaseRef.current;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      controller.filter(key, fn as any, { resetPage });
    }, []);

    const fromUrl = useCallback((): string | null => {
      return controller.getUrlParam(key) ?? options?.defaultValue ?? null;
    }, []);

    const toUrl = useCallback((value: string | null) => {
      controller.setUrlParams({ [key]: value });
    }, []);

    const subscribe = useCallback(
      (listener: () => void) => controller.subscribe(listener),
      []
    );

    const readSiblingUrl = useCallback(
      (siblingKey: string) => controller.getUrlParam(siblingKey),
      []
    );

    const getFilteredRepository = useCallback(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      () => controller.getRepositoryExcept(key) as unknown as TRepo,
      []
    );

    return createElement(component, {
      filter: boundFilter,
      disabled,
      fromUrl,
      toUrl,
      subscribe,
      readSiblingUrl,
      getFilteredRepository,
    });
  };

  return Wrapped;
}
